import React, {useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {MainHeader, PageWrapperLayout} from 'presentation/components';
import {FeaturesEnum, HomeEnum, MyPaymentsEnum, ServicesEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {useContractStatus, useHostName} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {BillsTaggingEnum} from 'main/analytics/gaTagsEnum';
import {useAccountInformation} from '../hooks';
import {useNewBillsBemobiFeatureFlag} from '../hooks/useNewBillsBemobiFeatureFlag';

const {myFinances} = translations['pt-br'].privateFlow.services;
const {
	openBillsPage,
	invoicesTitle,
	historyPage,
	billDetailsPage,
	coparticipationPage,
	coparticipationTabsPage,
	incomeTaxPage,
	historyPageNotredameSaude,
	invoiceDetailsPage,
} = translations['pt-br'].privateFlow.myFinancial;

function MyPayments() {
	const navigate = useNavigate();
	const {pathname, state: routeState} = useLocation();
	const {isResponsibleWithoutBenefits} = useAccountInformation();
	const {isValid} = useContractStatus();
	const {isEnabled: newBillsFlagIsEnabled} = useNewBillsBemobiFeatureFlag();
	const {isNotredameSaude} = useHostName();
	const {isEnabled: isNewCoparticipationEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_COPART,
	);

	const isHistoryPath = pathname.includes('historico-de-pagamentos');
	const isBilletDetailsPath = pathname.includes('detalhes-do-boleto');
	const isIncomeTaxPath = pathname.includes('imposto-de-renda');
	const isCoparticipationPath = pathname.includes('extrato-de-coparticipacao');

	const pageTitle = () => {
		if (isHistoryPath && isNotredameSaude) {
			return historyPageNotredameSaude;
		}

		if (isHistoryPath) {
			return newBillsFlagIsEnabled ? invoicesTitle : historyPage;
		}

		if (isBilletDetailsPath) {
			return isNotredameSaude ? invoiceDetailsPage : billDetailsPage;
		}

		if (isIncomeTaxPath) {
			return incomeTaxPage;
		}

		if (isCoparticipationPath) {
			return isNewCoparticipationEnabled ? coparticipationTabsPage : coparticipationPage;
		}

		return newBillsFlagIsEnabled || isNotredameSaude ? invoicesTitle : openBillsPage;
	};
	const getBackButtonTagId = () => {
		if (isBilletDetailsPath) {
			const saoLucasRouteState = routeState as {isSaoLucasBill?: boolean} | null;
			if (saoLucasRouteState?.isSaoLucasBill) {
				return BillsTaggingEnum.CLICK_VOLTAR_DETALHES_BOLETO_SAO_LUCAS;
			}
			return BillsTaggingEnum.CLICK_VOLTAR_DETALHES_BOLETO;
		}

		if (pageTitle() === openBillsPage) {
			return BillsTaggingEnum.CLICK_VOLTAR_PAGINA_BOLETO;
		}

		return undefined;
	};

	const handleBack = useCallback(() => {
		if (isHistoryPath || isBilletDetailsPath) {
			navigate(MyPaymentsEnum.OPEN_BILLS);
			return;
		}
		if ((isResponsibleWithoutBenefits || !isValid) && !routeState) {
			navigate(HomeEnum.HOME_PAGE);
			return;
		}
		if (routeState) {
			navigate(routeState);
			return;
		}
		navigate(ServicesEnum.SERVICES);
	}, [routeState, isHistoryPath, isBilletDetailsPath]);

	return (
		<PageWrapperLayout title={myFinances}>
			<MainHeader
				dataTagId={{
					backButtonRoute: getBackButtonTagId(),
				}}
				title={pageTitle()}
				handleBack={handleBack}
				hideProfileMenu
			/>
		</PageWrapperLayout>
	);
}

export {MyPayments};
